import React from 'react'
import { TouchableOpacity, View } from 'react-native'
import { useTheme } from '@ui-kitten/components'
import { StyleService, useStyleSheet } from '@style/service'
import { Icon, Text } from '@components/base'
import { useIsDarkMode } from '@config/theme'
import { ACTION_CARDS, BULLET_POINTS, SYNC_OPTIONS } from './constants'
import { ActionCard } from './ActionCard'
import { SyncOption } from './SyncOption'

interface ReportsNotEligibleCardProps {
  onDismiss: () => void
}

export const ReportsNotEligibleCard = ({ onDismiss }: ReportsNotEligibleCardProps) => {
  const styles = useStyleSheet(themedStyle)
  const theme = useTheme()
  const isDarkMode = useIsDarkMode()

  return (
    <View style={[styles.container, isDarkMode && styles.containerDark]}>
      <TouchableOpacity
        style={styles.dismissButton}
        onPress={onDismiss}
        accessibilityLabel="Dismiss"
      >
        <View style={styles.dismissIconHighlight}>
          <Icon name="x" size="14" color={theme['theme.text.tertiary']} />
        </View>
      </TouchableOpacity>

      <View style={styles.titleRow}>
        <View style={styles.lockIconHighlight}>
          <Icon name="lock-simple" size="14" color={theme['theme.text.primary']} />
        </View>
        <Text type="large" bold style={styles.flex1}>
          Add data to unlock this report
        </Text>
      </View>

      <Text type="regular" style={styles.subtitle}>
        To generate your Reports we need more information.
      </Text>

      <View style={styles.bulletPoints}>
        {BULLET_POINTS.map(({ text, color }) => (
          <View key={text} style={styles.bulletRow}>
            <View style={[styles.bullet, { backgroundColor: theme[color] }]} />
            <Text type="regular">{text}</Text>
          </View>
        ))}
      </View>

      <View style={styles.actionCards}>
        {ACTION_CARDS.map((item) => (
          <ActionCard key={item.label} item={item} />
        ))}
      </View>

      <View style={[styles.divider, isDarkMode && styles.dividerDark]} />

      <Text type="regular" bold style={styles.syncTitle}>
        Sync data from
      </Text>

      <View style={styles.syncRow}>
        {SYNC_OPTIONS.map((item) => (
          <SyncOption key={item.label} item={item} />
        ))}
      </View>
    </View>
  )
}

const themedStyle = StyleService.create({
  container: {
    backgroundColor: 'theme.background',
    borderRadius: 12,
    padding: 16,
  },
  containerDark: {
    backgroundColor: 'theme.surface.base1',
  },
  dismissButton: {
    position: 'absolute',
    top: 12,
    right: 12,
    zIndex: 1,
  },
  dismissIconHighlight: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: 'theme.surface.base2',
    alignItems: 'center',
    justifyContent: 'center',
  },
  lockIconHighlight: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: 'theme.surface.base1',
    alignItems: 'center',
    justifyContent: 'center',
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginRight: 36,
  },
  flex1: {
    flex: 1,
  },
  subtitle: {
    color: 'theme.text.secondary',
    marginTop: 8,
  },
  bulletPoints: {
    marginTop: 12,
    gap: 8,
  },
  bulletRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  bullet: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  actionCards: {
    marginTop: 16,
    gap: 8,
  },
  divider: {
    height: 1,
    backgroundColor: 'theme.surface.base1',
    marginVertical: 16,
  },
  dividerDark: {
    backgroundColor: 'theme.surface.base2',
  },
  syncTitle: {
    marginBottom: 12,
    textTransform: 'uppercase',
    color: 'theme.text.tertiary',
  },
  syncRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
})
